<template>
  <hb-section :title="$t('influencer_panel.commission_per_day')">
    <template #actions>
      <div class="commission-chart__legend">
        <span class="commission-chart__legend-item">
          <span class="commission-chart__dot" />
          {{ currentLabel }}
          <hb-money :cents="currentTotalCents" size="sm" />
        </span>
        <span v-if="hasPrevious" class="commission-chart__legend-item commission-chart__legend-item--muted">
          <span class="commission-chart__dash" />
          {{ $t('influencer_panel.previous_period') }}
          <hb-money :cents="previousTotalCents" size="sm" />
        </span>
      </div>
    </template>

    <vue-apex-charts type="area" height="240" :options="chartOptions" :series="series" />
  </hb-section>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { $themeColors } from '@themeConfig'
import HbSection from '@/components/hubee/HbSection.vue'
import HbMoney from '@/components/hubee/HbMoney.vue'
import { parseApiDate } from '../panel'

/**
 * Comissao gerada por dia, com o periodo anterior tracejado atras.
 *
 * O `vue-apexcharts` nao e registrado globalmente neste projeto — cada tela importa o
 * seu, como faz o dashboard financeiro.
 */
export default {
  name: 'CommissionChart',
  components: { VueApexCharts, HbSection, HbMoney },
  props: {
    current: { type: Array, default: () => [] },
    previous: { type: Array, default: () => [] },
    currentLabel: { type: String, default: '' },
    /**
     * O blur do CSS nao alcanca o SVG do apex: os rotulos do eixo e o tooltip sao nos
     * proprios do grafico, sem a classe `hb-money`. Por isso o estado chega por prop.
     */
    moneyHidden: { type: Boolean, default: false },
  },
  computed: {
    hasPrevious() {
      return this.previous.length > 0
    },
    currentTotalCents() {
      return this.current.reduce((sum, point) => sum + Number(point.commission_cents || 0), 0)
    },
    previousTotalCents() {
      return this.previous.reduce((sum, point) => sum + Number(point.commission_cents || 0), 0)
    },
    categories() {
      return this.current.map(point => {
        const date = parseApiDate(point.date)

        return date ? String(date.getDate()).padStart(2, '0') : ''
      })
    },
    series() {
      const toReais = points => points.map(point => Number(point.commission_cents || 0) / 100)

      const result = [{ name: this.currentLabel, data: toReais(this.current) }]

      if (this.hasPrevious) {
        // Alinhado por indice, nao por data: meses tem tamanhos diferentes, e casar pelo
        // dia do mes deslocaria a curva anterior contra o eixo atual.
        const previous = toReais(this.previous).slice(0, this.current.length)
        while (previous.length < this.current.length) previous.push(null)

        result.push({ name: this.$t('influencer_panel.previous_period'), data: previous })
      }

      return result
    },
    /**
     * Objeto novo a cada mudanca, de proposito: o vue-apexcharts 1.6 dispara
     * `updateOptions` na troca de identidade — mutar em lugar nao redesenha nada.
     */
    chartOptions() {
      const muted = '#b9b9c3'
      const money = value => (value === null || value === undefined
        ? ''
        : Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }))

      return {
        chart: {
          type: 'area',
          height: 240,
          parentHeightOffset: 0,
          toolbar: { show: false },
          zoom: { enabled: false },
          fontFamily: 'Montserrat, Helvetica, Arial, sans-serif',
          dropShadow: { enabled: false },
          animations: { easing: 'easeout', speed: 400 },
        },
        colors: [$themeColors.primary || '#8bb2db', muted],
        stroke: { curve: 'smooth', width: [2.5, 1.5], dashArray: [0, 5] },
        fill: {
          type: ['gradient', 'solid'],
          // A serie anterior fica so no traco: dois preenchimentos empilhados viram
          // borrao e escondem justamente a comparacao.
          opacity: [1, 0],
          gradient: {
            shadeIntensity: 0.9, opacityFrom: 0.32, opacityTo: 0.02, stops: [0, 90, 100],
          },
        },
        dataLabels: { enabled: false },
        markers: { size: 0, hover: { size: 4 } },
        grid: {
          borderColor: '#ebe9f1',
          strokeDashArray: 3,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: { left: 0, right: 0, top: -8 },
        },
        xaxis: {
          categories: this.categories,
          tickAmount: 6,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: muted, fontSize: '11px' } },
          crosshairs: { stroke: { color: '#ebe9f1', dashArray: 3 } },
        },
        yaxis: {
          show: !this.moneyHidden,
          tickAmount: 4,
          labels: {
            style: { colors: muted, fontSize: '11px' },
            formatter: value => money(value),
          },
        },
        legend: { show: false },
        tooltip: {
          shared: true,
          intersect: false,
          y: { formatter: value => (this.moneyHidden ? '•••' : money(value)) },
        },
      }
    },
  },
}
</script>

<style scoped>
.commission-chart__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  font-size: var(--fs-sm);
  color: var(--text-body);
}

.commission-chart__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
}

.commission-chart__legend-item--muted {
  color: var(--text-muted);
}

.commission-chart__dot {
  width: 10px;
  height: 10px;
  border-radius: var(--radius-pill);
  background: var(--primary);
}

.commission-chart__dash {
  width: 14px;
  height: 2px;
  background: var(--text-muted);
}
</style>
